import { z } from "zod";

/**
 * The payload of a `key`/`value` update operation: one option per updatable field, so
 * that `value` is typed and validated against the field `key` actually names.
 */
export type KeyValueUpdatePayload<
  FIELDS extends z.ZodRawShape,
  SHARED extends z.ZodRawShape,
> = {
  [K in keyof FIELDS & string]: z.infer<z.ZodObject<SHARED>> & {
    key: K;
    value: z.infer<FIELDS[K]>;
  };
}[keyof FIELDS & string];

/**
 * Payload schema for a hand-written `key`/`value` update operation, where the type of
 * `value` depends on which field `key` names.
 *
 * One option per field, rather than `key: enum` paired with `value: union of all field
 * types`, is what makes the schema worth having: it rejects a value that is valid for
 * some other field but not the one `key` actually names.
 *
 * `z.union` rather than `z.discriminatedUnion` because the options are built from a
 * runtime record, so they carry an index signature instead of the statically known
 * `key` property `discriminatedUnion` requires. Validation is equivalent — every option
 * pins `key` to its own literal, so at most one can match.
 */
export function keyValueUpdatePayloadSchema<
  FIELDS extends z.ZodRawShape,
  SHARED extends z.ZodRawShape = Record<string, never>,
>({
  fields,
  shared = {} as SHARED,
}: {
  /** Field name to the schema for that field's value. */
  fields: FIELDS;
  /** Extra payload properties present on every option, such as an entity id. */
  shared?: SHARED;
}): z.ZodType<KeyValueUpdatePayload<FIELDS, SHARED>, z.ZodTypeDef, unknown> {
  const options = Object.entries(fields).map(([key, value]) =>
    z.object({ ...shared, key: z.literal(key), value }),
  );

  const [first, second, ...rest] = options;
  if (first == null) {
    throw new Error("keyValueUpdatePayloadSchema needs at least one field");
  }
  const schema = second == null ? first : z.union([first, second, ...rest]);

  // The options are built by iterating a runtime record, which loses the literal type
  // of each key, so the correlation between `key` and `value` has to be asserted.
  return schema as z.ZodType<
    KeyValueUpdatePayload<FIELDS, SHARED>,
    z.ZodTypeDef,
    unknown
  >;
}
